import GloLayout from 'lowcode/common/GloLayout';
import { useRouter } from 'next/router';
import { useEffect, useState, useRef } from 'react';
import { queryBlogTagList } from '../../common/blog-api/queryCategoryItems';
import { getIsPadWidth } from '@elem/utils/devices';
import { LangText } from '../../common/blog-lang-text';
import './index.scss';

export default function Content({ id, data }: any) {
    const { locale = 'us' } = useRouter();
    const { tagItems = [] } = data;
    const langText = LangText[locale] || LangText['us'];

    // 初始化时通过 id 匹配 tagItems，确保 list 中的数据与 tagItems 同步
    const getInitialList = (dataList: any[], tags: any[]) => {
        if (!tags || tags.length === 0) return dataList || [];

        return (dataList || []).map((listItem: any) => {
            const matchedTag = tags.find((tagItem: any) =>
                (listItem.id && tagItem.id && listItem.id === tagItem.id) ||
                (listItem.tagUrl && tagItem.tagUrl && listItem.tagUrl === tagItem.tagUrl)
            );
            return matchedTag || listItem;
        }).filter((item: any) => {
            // 过滤掉在 tagItems 中不存在的项
            return tags.some((tagItem: any) =>
                (item.id && tagItem.id && item.id === tagItem.id) ||
                (item.tagUrl && tagItem.tagUrl && item.tagUrl === tagItem.tagUrl)
            );
        });
    };

    const [list, setList] = useState<any[]>(() => getInitialList(data.list, tagItems));
    const [showDropdown, setShowDropdown] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isPad, setIsPad] = useState(false);
    const [allTagItems, setAllTagItems] = useState<any[]>(tagItems);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchTags = async () => {
            if (!tagItems || tagItems.length === 0) {
                const dataA: any = await queryBlogTagList(locale, {});
                const items = dataA?.items || [];
                setAllTagItems(items);
            }
        };
        fetchTags();
    }, [locale]);

    // 同步 data.list 到 list 状态，并匹配 allTagItems
    useEffect(() => {
        if (data.list && allTagItems.length > 0) {
            const syncedList = getInitialList(data.list, allTagItems);
            setList(syncedList);
        }
    }, [data.list, allTagItems]);
    useEffect(() => {
        const checkPadWidth = () => {
            setIsPad(getIsPadWidth());
        };
        checkPadWidth();
        window.addEventListener('resize', checkPadWidth);
        return () => window.removeEventListener('resize', checkPadWidth);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (!isPad) {
                    setShowDropdown(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isPad]);

    // 计算并设置CSS变量 --sticky-top-offset（仅初始化时）
    useEffect(() => {
        let totalHeight = 0;

        // 获取所有sticky元素的高度
        const header = document.querySelector('.ecoflow-frame-header');
        if (header) {
            totalHeight += header.getBoundingClientRect().height;
        }

        // 如果当前容器也是sticky的，加上它的高度
        if (containerRef.current) {
            const style = window.getComputedStyle(containerRef.current);
            if (style.position === 'sticky' || style.position === 'fixed') {
                totalHeight += containerRef.current.getBoundingClientRect().height;
            }
        }

        // 设置CSS变量到根元素
        document.documentElement.style.setProperty('--sticky-top-offset', `${totalHeight}px`);
    }, []);

    // 计算需要放在All下拉菜单中的标签
    // 从allTagItems中过滤出不在list中的项目
    const listSlugs = list.map((item: any) => item.tagUrl);
    const remainingTags = allTagItems.filter((item: any) => {
        return !listSlugs.includes(item.tagUrl);
    });

    // 桌面端（>1024）：显示list，All中放剩余的标签
    // 移动端和pad端（<=1024）：全部放在All中，list在最前面
    const visibleTags = isPad ? [] : list;
    const hiddenTags = isPad ? [...list, ...remainingTags] : remainingTags;
    const hasMoreTags = hiddenTags.length > 0;

    const getCategoryUrl = (item: any) => {
        return `/${locale}/blog/category/${item.tagUrl}`;
    };

    const handleAllClick = () => {
        if (isPad) {
            setShowModal(true);
        } else {
            setShowDropdown(!showDropdown);
        }
    };

    const handleModalItemClick = () => {
        setShowModal(false);
    };

    // 检查文本是否被截断，如果是则显示title
    const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const target = e.currentTarget;
        if (target.scrollWidth > target.clientWidth) {
            target.setAttribute('title', target.textContent || '');
        }
    };

    return (
        <GloLayout className="editor-blog-category-nav" id={id}>
            <div className="editor-blog-category-nav-content" ref={containerRef}>
                <div className="editor-blog-category-nav-content-item">
                    {visibleTags.map((item: any, index: number) => (
                        <a
                            key={index}
                            href={getCategoryUrl(item)}
                            className="editor-blog-category-nav-content-item-title-text"
                            onMouseEnter={handleMouseEnter}
                        >
                            {item.tag}
                        </a>
                    ))}

                    {hasMoreTags && (
                        <div
                            ref={dropdownRef}
                            className={`editor-blog-category-nav-content-item-dropdown ${showDropdown ? 'active' : ''}`}
                            onMouseEnter={() => !isPad && setShowDropdown(true)}
                            onMouseLeave={() => !isPad && setShowDropdown(false)}
                            onClick={handleAllClick}
                        >
                            <div className="editor-blog-category-nav-content-item-title-text">
                                {langText.ALL}
                                <svg
                                    className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                >
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>

                            <div className="editor-blog-category-nav-content-item-dropdown-menu">
                                {hiddenTags.map((item: any, index: number) => (
                                    <a
                                        key={index}
                                        href={getCategoryUrl(item)}
                                        className="editor-blog-category-nav-content-item-dropdown-menu-item"
                                        onMouseEnter={handleMouseEnter}
                                    >
                                        {item.tag}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Overlay and Content */}
                {isPad && showModal && (
                    <>
                        <div className="editor-blog-category-modal-overlay" onClick={() => setShowModal(false)}></div>
                        <div className="editor-blog-category-modal">
                            <div className="editor-blog-category-modal-header">
                                <h3>
                                    <img
                                        src="https://websiteoss.ecoflow.com/upload/image/1761041493108/Group%2012.svg"
                                        alt={langText.BlogCenter}
                                        className="blog-center-icon"
                                    />
                                    {langText.BlogCenter}
                                </h3>
                                <button
                                    className="editor-blog-category-modal-close"
                                    onClick={() => setShowModal(false)}
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="editor-blog-category-modal-content">
                                {hiddenTags.map((item: any, index: number) => (
                                    <a
                                        key={index}
                                        href={getCategoryUrl(item)}
                                        className="editor-blog-category-modal-item"
                                        onClick={handleModalItemClick}
                                    >
                                        <span>{item.tag}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </GloLayout>
    );
}

